import {
  parseStudentDirectorySort,
  type StudentDirectorySort,
} from "@wmf/shared/studentDirectory";

export type StudentListScope = "mine" | "all";

export const STUDENT_PAGE_SIZES = ["25", "50", "100"] as const;
export const NO_UPCOMING = "none";

export interface StudentViewState {
  query: string;
  scope: StudentListScope;
  size: string;
  sort: StudentDirectorySort;
  tutor: string;
  instrument: string;
  school: string;
  status: string;
  account: string;
  upcoming: string;
}

export interface SavedStudentView {
  id: string;
  name: string;
  state: StudentViewState;
}

export interface StudentBuiltInView {
  id: string;
  name: string;
  state: StudentViewState;
}

const PREFIX = "wmf.studentViews.v1.";

export function emptyStudentViewState(scope: StudentListScope): StudentViewState {
  return {
    query: "",
    scope,
    size: "25",
    sort: "name",
    tutor: "",
    instrument: "",
    school: "",
    status: "",
    account: "",
    upcoming: "",
  };
}

/**
 * Operational shortcuts whose cohort can be answered by the student-list query.
 * Payment due is deliberately absent: the Hub student mirror does not own an
 * authoritative student-level payment-due cohort, so presenting one here would
 * turn a partial client-side inference into an operational fact.
 */
export function builtInStudentViews(scope: StudentListScope): StudentBuiltInView[] {
  const base = emptyStudentViewState(scope);
  return [
    { id: "builtin:all", name: "All students", state: base },
    {
      id: "builtin:no-upcoming",
      name: "No upcoming lesson",
      state: { ...base, upcoming: NO_UPCOMING },
    },
    {
      id: "builtin:no-account",
      name: "No Hub account linked",
      state: { ...base, account: "no" },
    },
    {
      id: "builtin:inactive",
      name: "Inactive",
      state: { ...base, status: "inactive" },
    },
  ];
}

export function sameStudentViewState(a: StudentViewState, b: StudentViewState): boolean {
  return (
    a.query === b.query &&
    a.scope === b.scope &&
    a.size === b.size &&
    a.sort === b.sort &&
    a.tutor === b.tutor &&
    a.instrument === b.instrument &&
    a.school === b.school &&
    a.status === b.status &&
    a.account === b.account &&
    a.upcoming === b.upcoming
  );
}

function validScope(value: unknown): value is StudentListScope {
  return value === "mine" || value === "all";
}

function validSize(value: unknown): value is (typeof STUDENT_PAGE_SIZES)[number] {
  return typeof value === "string" && STUDENT_PAGE_SIZES.includes(value as (typeof STUDENT_PAGE_SIZES)[number]);
}

const LARGEST_STUDENT_PAGE_SIZE = STUDENT_PAGE_SIZES[STUDENT_PAGE_SIZES.length - 1] as (typeof STUDENT_PAGE_SIZES)[number];

// Pre-existing saved views may carry a "size" from before the page-size
// picker's options changed (e.g. the removed "all" option). Migrate those to
// the largest remaining size instead of discarding the whole saved view.
function normalizedSize(value: unknown): (typeof STUDENT_PAGE_SIZES)[number] {
  return validSize(value) ? value : LARGEST_STUDENT_PAGE_SIZE;
}

function stringValue(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

export function parseSavedStudentView(value: unknown): SavedStudentView | null {
  if (!value || typeof value !== "object") return null;
  const candidate = value as Record<string, unknown>;
  const rawState = candidate["state"];
  if (!rawState || typeof rawState !== "object") return null;
  const state = rawState as Record<string, unknown>;
  const id = stringValue(candidate["id"])?.trim();
  const name = stringValue(candidate["name"])?.trim();
  if (!id || !name || !validScope(state["scope"])) return null;

  const fields = ["query", "tutor", "instrument", "school", "status", "account", "upcoming"] as const;
  if (fields.some((field) => typeof state[field] !== "string")) return null;

  return {
    id,
    name,
    state: {
      query: state["query"] as string,
      scope: state["scope"],
      size: normalizedSize(state["size"]),
      // Existing v1 browser-local views predate Brief 12, so an absent sort is
      // intentionally read as the stable name default rather than discarded.
      sort: parseStudentDirectorySort(state["sort"]),
      tutor: state["tutor"] as string,
      instrument: state["instrument"] as string,
      school: state["school"] as string,
      status: state["status"] as string,
      account: state["account"] as string,
      upcoming: state["upcoming"] as string,
    },
  };
}

function browserStorage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

function storageKey(accountId: string): string {
  return PREFIX + accountId;
}

export function readSavedStudentViews(
  accountId: string,
  storage: Storage | null = browserStorage(),
): SavedStudentView[] {
  try {
    const raw = storage?.getItem(storageKey(accountId));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(parseSavedStudentView).filter((view): view is SavedStudentView => view !== null);
  } catch {
    return [];
  }
}

export function writeSavedStudentViews(
  accountId: string,
  views: SavedStudentView[],
  storage: Storage | null = browserStorage(),
): void {
  try {
    storage?.setItem(storageKey(accountId), JSON.stringify(views));
  } catch {
    // A browser that refuses local storage simply does not persist custom views.
  }
}

function comparableViewName(name: string): string {
  return name.trim().toLocaleLowerCase();
}

export function savedStudentViewNameTaken(
  views: SavedStudentView[],
  name: string,
  exceptId?: string,
): boolean {
  const wanted = comparableViewName(name);
  if (!wanted) return false;
  return views.some(
    (view) => view.id !== exceptId && comparableViewName(view.name) === wanted,
  );
}

export function addSavedStudentView(
  views: SavedStudentView[],
  name: string,
  state: StudentViewState,
  id: string,
): SavedStudentView[] {
  const cleanName = name.trim();
  if (!cleanName || savedStudentViewNameTaken(views, cleanName)) return views;
  return [...views, { id, name: cleanName, state: { ...state } }];
}

export function renameSavedStudentView(
  views: SavedStudentView[],
  id: string,
  name: string,
): SavedStudentView[] {
  const cleanName = name.trim();
  if (!cleanName || savedStudentViewNameTaken(views, cleanName, id)) return views;
  return views.map((view) => (view.id === id ? { ...view, name: cleanName } : view));
}

export function deleteSavedStudentView(views: SavedStudentView[], id: string): SavedStudentView[] {
  return views.filter((view) => view.id !== id);
}

export function studentViewStateForId(
  viewId: string | null,
  builtIns: StudentBuiltInView[],
  savedViews: SavedStudentView[],
): StudentViewState | null {
  if (!viewId) return null;
  const builtIn = builtIns.find((view) => view.id === viewId);
  if (builtIn) return builtIn.state;
  if (!viewId.startsWith("custom:")) return null;
  return savedViews.find((view) => `custom:${view.id}` === viewId)?.state ?? null;
}

/**
 * Read only non-sensitive categorical state from the URL. Free-text search is
 * intentionally excluded because staff commonly type names or email addresses.
 */
export function studentViewStateFromParams(
  params: URLSearchParams,
  scope: StudentListScope,
  query: string,
): StudentViewState {
  const size = params.get("size");
  return {
    query,
    scope,
    size: validSize(size) ? size : "25",
    sort: parseStudentDirectorySort(params.get("sort")),
    tutor: scope === "all" ? params.get("tutor") ?? "" : "",
    instrument: params.get("instrument") ?? "",
    school: params.get("school") ?? "",
    status: params.get("status") ?? "",
    account: params.get("account") ?? "",
    upcoming: params.get("upcoming") === NO_UPCOMING ? NO_UPCOMING : "",
  };
}

/**
 * Put safe view state in the URL while retaining unrelated launch parameters.
 * Search text is never serialised here. Page is deliberately reset whenever a
 * view/filter/sort changes, because the old page number describes another set.
 */
export function paramsForStudentView(
  current: URLSearchParams,
  state: StudentViewState,
  activeViewId: string | null,
): URLSearchParams {
  const next = new URLSearchParams(current);
  for (const key of ["scope", "size", "sort", "tutor", "instrument", "school", "status", "account", "upcoming", "view", "page"]) {
    next.delete(key);
  }

  if (state.scope === "mine") next.set("scope", "mine");
  if (state.size !== "25") next.set("size", state.size);
  if (state.sort !== "name") next.set("sort", state.sort);
  if (state.scope === "all" && state.tutor) next.set("tutor", state.tutor);
  if (state.instrument) next.set("instrument", state.instrument);
  if (state.school) next.set("school", state.school);
  if (state.status) next.set("status", state.status);
  if (state.account) next.set("account", state.account);
  if (state.upcoming === NO_UPCOMING) next.set("upcoming", NO_UPCOMING);
  if (activeViewId) next.set("view", activeViewId);
  return next;
}
